// src/pages/ArtistProfile.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Stack,
  Button,
  Chip,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Container,
  Divider,
  Paper,
  Avatar,
  Skeleton,
  Tooltip,
  Snackbar,
  Alert,
  Rating,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ShareIcon from "@mui/icons-material/Share";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import PriceChangeIcon from "@mui/icons-material/PriceChange";
import LinkIcon from "@mui/icons-material/Link";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import PlaceIcon from "@mui/icons-material/Place";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import YouTubeIcon from "@mui/icons-material/YouTube";
import AudiotrackIcon from "@mui/icons-material/Audiotrack";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import EditIcon from "@mui/icons-material/Edit";
import AvailabilityList from "../components/AvailabilityList";
import { useNavigate, useParams } from "react-router-dom";
import {
  fetchArtistById as getArtist,
  fetchArtistEvents as getArtistEvents,
  fetchArtistReviews as getArtistReviews,
  fetchSimilarArtists as getSimilarArtists,
} from "../services/artist.service";
import { hasPermission, PERMISSIONS } from "../services/authRoles";
import { buildRecipientInboxPath } from "../services/messaging";
import { profileService } from "../services/resources.service";
import { managesPage } from "../services/profilePageSetup";
import { formatDate, formatDateTime, formatMoneyRange, getMessage, useLocale } from "../i18n/locale";
import FollowButton from "../components/FollowButton";
import ProfileRating from "../components/ProfileRating";
import EventReviewDialog from "../components/EventReviewDialog";
import ProfileGallery from "../components/ProfileGallery";
import LinkedAccountBadge from "../components/LinkedAccountBadge";
import { BANNER_IMAGE_FALLBACK, getBannerImage, getProfileImage, imageFallbackHandler, PROFILE_IMAGE_FALLBACK } from "../services/profileImages";
import { getPortfolioImage, PORTFOLIO_FALLBACKS } from "../services/portfolioImages";

const fetchArtistById = getArtist;
const fetchArtistEvents = getArtistEvents;
const fetchReviews = getArtistReviews;
const fetchSimilarArtists = getSimilarArtists;

// ---------------- UI helpers ----------------
function FeeChip({ min, max, currency }) {
  const localeContext = useLocale();
  if ((min === null || min === undefined || min === "") && (max === null || max === undefined || max === "")) return null;

  return (
    <Chip
      size="small"
      icon={<PriceChangeIcon />}
      label={formatMoneyRange(min, max, { locale: localeContext.locale, currency: currency || localeContext.currency })}
      variant="outlined"
    />
  );
}

function StatChip({ icon, label }) {
  return <Chip size="small" icon={icon} label={label} />;
}

function MediaCard({ m }) {
  const isVideo = m.type === "video";
  const fallbackImage = PORTFOLIO_FALLBACKS[isVideo ? "video" : "audio"];
  return (
    <Card variant="outlined" sx={{ borderRadius: 3, height: "100%" }}>
      <CardMedia
        component="img"
        height="140"
        image={getPortfolioImage(m)}
        onError={imageFallbackHandler(fallbackImage)}
        alt={m.title}
        loading="lazy"
      />
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1}>
          {isVideo ? <YouTubeIcon /> : <AudiotrackIcon />}
          <Typography variant="subtitle2" fontWeight={700} noWrap>
            {m.title}
          </Typography>
        </Stack>
        <Button
          size="small"
          variant="outlined"
          sx={{ mt: 1 }}
          endIcon={<OpenInNewIcon />}
          onClick={() => window.open(m.url, "_blank", "noopener,noreferrer")}
        >
          {isVideo ? "Watch" : "Listen"}
        </Button>
      </CardContent>
    </Card>
  );
}

function EventMiniCard({ evt, onOpen }) {
  const locale = useLocale();
  return (
    <Card variant="outlined" sx={{ borderRadius: 3 }}>
      <CardMedia component="img" height="110" image={evt.poster || BANNER_IMAGE_FALLBACK} onError={imageFallbackHandler(BANNER_IMAGE_FALLBACK)} alt={evt.title} loading="lazy" />
      <CardContent>
        <Typography variant="subtitle2" color="text.secondary">
          {formatDateTime(evt.startAt, locale)}
        </Typography>
        <Typography variant="subtitle1" fontWeight={800} noWrap>
          {evt.title}
        </Typography>
        <Typography variant="body2" color="text.secondary" noWrap>
          {evt.venue?.name} ({evt.venue?.city})
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
          {evt.tags?.map((t) => (
            <Chip key={t} label={t} size="small" />
          ))}
        </Stack>
      </CardContent>
      <Box sx={{ px: 2, pb: 2 }}>
        <Button fullWidth variant="outlined" onClick={() => onOpen(evt.id)}>
          Open
        </Button>
      </Box>
    </Card>
  );
}

function SimilarArtistCard({ a, onOpen }) {
  return (
    <Card variant="outlined" sx={{ borderRadius: 3 }}>
      <CardContent>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Avatar src={a.avatar} alt={a.name} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Typography variant="subtitle1" fontWeight={800} noWrap>{a.name}</Typography>
              <LinkedAccountBadge ownerUserId={a.ownerUserId} />
            </Stack>
            <Typography variant="body2" color="text.secondary" noWrap>
              {a.city} • {a.genres.join(", ")}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5, flexWrap: "wrap" }}>
              {a.rating && (
                <>
                  <Rating
                    value={a.rating || 0}
                    precision={0.1}
                    size="small"
                    readOnly
                    icon={<StarRoundedIcon fontSize="inherit" />}
                    emptyIcon={<StarRoundedIcon fontSize="inherit" />}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {a.rating?.toFixed?.(1) ?? "–"}
                  </Typography>
                </>
              )}
              <FeeChip min={a.feeMin} max={a.feeMax} currency={a.currency} />
            </Stack>
          </Box>
        </Stack>
      </CardContent>
      <Box sx={{ px: 2, pb: 2 }}>
        <Button fullWidth variant="outlined" onClick={() => onOpen(a.id)}>
          View profile
        </Button>
      </Box>
    </Card>
  );
}

// ---------------- Page ----------------
export default function ArtistProfile({ onRequireAuth, user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { locale } = useLocale();

  const [artist, setArtist] = useState(null);
  const [events, setEvents] = useState(null);
  const [reviews, setReviews] = useState(null);
  const [similar, setSimilar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isOwner, setIsOwner] = useState(false);
  const [tab, setTab] = useState(0); // 0 Overview, 1 Media, 2 Reviews
  const [snack, setSnack] = useState({ open: false, msg: "", severity: "info" });
  const [reviewOpen, setReviewOpen] = useState(false);
  const [documentDownloadBusy, setDocumentDownloadBusy] = useState(false);
  const { upcomingEvents, pastEvents } = useMemo(() => {
    const now = Date.now();
    const validEvents = (events || []).filter((event) => Number.isFinite(Date.parse(event.startAt)));
    return {
      upcomingEvents: validEvents.filter((event) => Date.parse(event.startAt) >= now),
      pastEvents: validEvents.filter((event) => Date.parse(event.startAt) < now).reverse(),
    };
  }, [events]);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [a, e, r, s] = await Promise.all([
          fetchArtistById(id),
          fetchArtistEvents(id),
          fetchReviews(id),
          fetchSimilarArtists(id),
        ]);
        if (!active) return;
        setArtist(a);
        setEvents(e);
        setReviews(r);
        setSimilar(s);
      } catch (err) {
        if (!active) return;
        setError(err.message);
        setSnack({
          open: true,
          msg: "Artist not found",
          severity: "error",
        });
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [id]);

  useEffect(() => {
    let active = true;
    setIsOwner(false);
    if (user?.role !== "ARTIST") return () => { active = false; };

    profileService.me()
      .then((profile) => {
        if (active) setIsOwner(managesPage(profile, "artistPages", id));
      })
      .catch(() => {
        if (active) setIsOwner(false);
      });

    return () => { active = false; };
  }, [id, user?.id, user?.role]);

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: artist?.name ?? "Artist", url });
      } catch {}
    } else {
      await navigator.clipboard.writeText(url);
      setSnack({ open: true, msg: "Link copied to clipboard", severity: "info" });
    }
  };

  const technicalDownloadsEnabled = artist?.technicalDownloadsEnabled === true;
  const techRiderUrl = artist?.techRider?.url || artist?.techRiderUrl;
  const stageLayoutUrl = artist?.stageLayout?.url || artist?.stageLayoutUrl || artist?.stagePlot?.url || artist?.stagePlotUrl;

  const downloadTechnicalDocument = async (selectUrl) => {
    if (documentDownloadBusy) return;
    setDocumentDownloadBusy(true);
    try {
      // Signed S3 links expire. Always obtain a new one when the visitor clicks,
      // and bypass any intermediary/browser cache holding an older API response.
      const freshArtist = await fetchArtistById(id, {
        query: { downloadToken: Date.now() },
        headers: { "Cache-Control": "no-cache" },
      });
      const url = selectUrl(freshArtist);
      if (!url) throw new Error("Document is unavailable");
      window.location.assign(url);
    } catch {
      setSnack({
        open: true,
        msg: getMessage(locale, "technicalDocumentDownloadError"),
        severity: "error",
      });
    } finally {
      setDocumentDownloadBusy(false);
    }
  };

  // Show error state
  if (error && !loading) {
    return (
      <Box sx={{ minHeight: "100dvh", bgcolor: "background.default" }}>
        <AppBar position="sticky" elevation={0} color="inherit">
          <Toolbar>
            <IconButton edge="start" onClick={() => navigate(-1)}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" sx={{ fontWeight: 900 }}>
              Artist Not Found
            </Typography>
          </Toolbar>
        </AppBar>
        <Container maxWidth="lg" sx={{ py: 4, textAlign: "center" }}>
          <Typography variant="h5" color="text.secondary" gutterBottom>
            Artist not found
          </Typography>
          <Button variant="contained" onClick={() => navigate("/artists")} sx={{ mt: 2 }}>
            Browse Artists
          </Button>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100dvh", bgcolor: "background.default" }}>
      <AppBar position="sticky" elevation={0} color="inherit">
        <Toolbar
          sx={{
            gap: 1,
            width: "100%",
            maxWidth: 1248,
            mx: "auto",
            px: { xs: 1.5, sm: 3 },
          }}
        >
          <IconButton edge="start" aria-label="back" onClick={() => navigate(-1)}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" noWrap sx={{ fontWeight: 900, letterSpacing: 0.2 }}>
            Artist
          </Typography>
          <Box sx={{ ml: "auto", display: "flex", gap: 1 }}>
            {isOwner && (
              <Button
                variant="contained"
                size="small"
                startIcon={<EditIcon />}
                onClick={() => navigate(`/artists/${id}/edit`)}
              >
                {getMessage(locale, "artistProfileEdit")}
              </Button>
            )}
            <FollowButton
              subjectType="artist"
              subjectId={id}
              user={user}
              redirectTo={`/artists/${id}`}
              onRequireAuth={onRequireAuth}
              onFeedback={(msg, severity) => setSnack({ open: true, msg, severity })}
              followerCount={artist?.followerCount}
            />
            <Tooltip title="Share">
              <IconButton onClick={share} aria-label="share">
                <ShareIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ pt: { xs: 0, sm: 3 }, px: { xs: 0, sm: 3 } }}>
        {/* Hero */}
        <Box sx={{ position: "relative" }}>
          {loading ? (
            <Skeleton
              variant="rectangular"
              sx={{
                height: { xs: 200, sm: 280, md: 360 },
                borderRadius: { xs: 0, sm: 3 },
              }}
            />
          ) : (
            <>
              <Box
                role="img"
                aria-label={`${artist.name} artist`}
                sx={{
                  height: { xs: 200, sm: 280, md: 360 },
                  borderRadius: { xs: 0, sm: 3 },
                  bgcolor: "secondary.main",
                  backgroundImage: `linear-gradient(180deg, rgba(29,33,40,.04), rgba(29,33,40,.44)), url(${getBannerImage(artist)})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              />
              <Avatar
                src={getProfileImage(artist)}
                imgProps={{ onError: imageFallbackHandler(PROFILE_IMAGE_FALLBACK) }}
                alt={artist.name}
                sx={{
                  width: { xs: 88, sm: 112 },
                  height: { xs: 88, sm: 112 },
                  position: "absolute",
                  left: { xs: 16, sm: 32 },
                  bottom: { xs: -44, sm: -56 },
                  border: (theme) => `4px solid ${theme.palette.background.default}`,
                  boxShadow: "0 8px 24px rgba(29,33,40,.18)",
                }}
              />
            </>
          )}
        </Box>

        <Box sx={{ pt: { xs: 6, sm: 8 }, pb: 2, px: { xs: 2, sm: 0 } }}>
        {/* Header */}
        {loading ? (
          <Stack spacing={1} sx={{ mb: 2 }}>
            <Skeleton width="40%" height={36} />
            <Skeleton width="30%" height={24} />
          </Stack>
        ) : (
          <Stack spacing={0.5} sx={{ mb: 2 }}>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <Typography variant="h5" fontWeight={900}>{artist.name}</Typography>
              <LinkedAccountBadge ownerUserId={artist.ownerUserId} sx={{ fontSize: "1.35rem" }} />
            </Stack>
            <Typography variant="body2" color="text.secondary">
              <PlaceIcon sx={{ fontSize: 16, mr: 0.5 }} />
              {artist.city} • {artist.genres.join(", ")}
            </Typography>
            <ProfileRating rating={artist.rating} reviewCount={reviews?.length} />
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap", mt: 1 }}>
              <FeeChip min={artist.feeMin} max={artist.feeMax} currency={artist.currency} />
              {artist.genres.map((g) => (
                <StatChip key={g} icon={<MusicNoteIcon />} label={g} />
              ))}
            </Stack>

            <Stack direction="row" spacing={0} sx={{ flexWrap: "wrap", pt: 1 }}>
              <Button
                size="small"
                variant="contained"
                sx={{ mr: 1, mt: 1 }}
                onClick={() => {
                  const redirectTo = buildRecipientInboxPath("artist", artist.id);
                  if (!user) return onRequireAuth?.({ redirectTo, reasonKey: "loginModalMessageReason" });
                  navigate(redirectTo);
                }}
              >
                {getMessage(locale, "venueContact")}
              </Button>
              {artist.links?.spotify && (
                <Button
                  size="small"
                  variant="outlined"
                  sx={{ mr: 1, mt: 1 }}
                  startIcon={<PlayCircleOutlineIcon />}
                  onClick={() => window.open(artist.links.spotify, "_blank", "noopener,noreferrer")}
                >
                  Spotify
                </Button>
              )}
              {artist.links?.youtube && (
                <Button
                  size="small"
                  variant="outlined"
                  sx={{ mr: 1, mt: 1 }}
                  startIcon={<YouTubeIcon />}
                  onClick={() => window.open(artist.links.youtube, "_blank", "noopener,noreferrer")}
                >
                  YouTube
                </Button>
              )}
              {artist.links?.instagram && (
                <Button
                  size="small"
                  variant="outlined"
                  sx={{ mr: 1, mt: 1 }}
                  onClick={() => window.open(artist.links.instagram, "_blank", "noopener,noreferrer")}
                >
                  Instagram
                </Button>
              )}
              {artist.links?.facebook && (
                <Button
                  size="small"
                  variant="outlined"
                  sx={{ mr: 1, mt: 1 }}
                  onClick={() => window.open(artist.links.facebook, "_blank", "noopener,noreferrer")}
                >
                  Facebook
                </Button>
              )}
              {artist.links?.soundcloud && (
                <Button
                  size="small"
                  variant="outlined"
                  sx={{ mr: 1, mt: 1 }}
                  startIcon={<LinkIcon />}
                  onClick={() => window.open(artist.links.soundcloud, "_blank", "noopener,noreferrer")}
                >
                  SoundCloud
                </Button>
              )}
              {artist.contact?.email && hasPermission(user, PERMISSIONS.CONTACT_ARTIST) && (
                <Button
                  size="small"
                  variant="contained"
                  sx={{ mr: 1, mt: 1 }}
                  onClick={() => window.open(`mailto:${artist.contact.email}`, "_self")}
                >
                  Email
                </Button>
              )}
              {artist.contact?.phone && hasPermission(user, PERMISSIONS.CONTACT_ARTIST) && (
                <Button
                  size="small"
                  sx={{ mt: 1 }}
                  variant="outlined"
                  onClick={() => window.open(`tel:${artist.contact.phone}`, "_self")}
                >
                  Call
                </Button>
              )}
            </Stack>
          </Stack>
        )}

        {/* Tabs */}
        <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ px: 1, borderBottom: 1, borderColor: "divider" }}
          >
            <Tab label="Overview" />
            <Tab label={getMessage(locale, "profileMedia")} />
            <Tab label="Reviews" />
          </Tabs>

          {/* Overview */}
          {tab === 0 && (
            <Box sx={{ p: 2 }}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 8 }}>
                  {loading ? (
                    <>
                      <Skeleton height={18} />
                      <Skeleton height={18} width="92%" />
                      <Skeleton height={18} width="80%" />
                    </>
                  ) : (
                    <>
                      <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1 }}>
                        About
                      </Typography>
                      <Typography variant="body1">{artist.bio}</Typography>
                      {artist.members?.length > 0 && (
                        <Box sx={{ mt: 3 }}>
                          <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1 }}>
                            {getMessage(locale, "artistProfileMembers")}
                          </Typography>
                          <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
                            {artist.members.map((member) => (
                              <Chip key={member} label={member} variant="outlined" />
                            ))}
                          </Stack>
                        </Box>
                      )}
                    </>
                  )}

                  {/* Upcoming / Past Events */}
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1 }}>
                      {getMessage(locale, "artistUpcomingEvents")}
                    </Typography>
                    {loading ? (
                      <Grid container spacing={2}>
                        {Array.from({ length: 2 }).map((_, i) => (
                          <Grid size={{ xs: 12, sm: 6 }} key={i}>
                            <Skeleton variant="rounded" height={200} sx={{ borderRadius: 3 }} />
                          </Grid>
                        ))}
                      </Grid>
                    ) : upcomingEvents.length ? (
                      <Grid container spacing={2}>
                        {upcomingEvents.map((evt) => (
                          <Grid size={{ xs: 12, sm: 6 }} key={evt.id}>
                            <EventMiniCard evt={evt} onOpen={(eid) => navigate(`/events/${eid}`)} />
                          </Grid>
                        ))}
                      </Grid>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {getMessage(locale, "artistNoUpcomingEvents")}
                      </Typography>
                    )}

                    <Divider sx={{ my: 2 }} />
                    <Typography component="h3" variant="subtitle1" fontWeight={800} sx={{ mb: 1 }}>
                      {getMessage(locale, "artistPastEvents")}
                    </Typography>
                    {loading ? (
                      <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <Skeleton variant="rounded" height={200} sx={{ borderRadius: 3 }} />
                        </Grid>
                      </Grid>
                    ) : pastEvents.length ? (
                      <Grid container spacing={2}>
                        {pastEvents.map((evt) => (
                          <Grid size={{ xs: 12, sm: 6 }} key={evt.id}>
                            <EventMiniCard evt={evt} onOpen={(eid) => navigate(`/events/${eid}`)} />
                          </Grid>
                        ))}
                      </Grid>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {getMessage(locale, "artistNoPastEvents")}
                      </Typography>
                    )}
                  </Box>
                </Grid>

                {/* Sidebar */}
                <Grid size={{ xs: 12, md: 4 }}>
                  {/* Tech Rider */}
                  <Paper variant="outlined" sx={{ borderRadius: 3, p: 2 }}>
                    <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1 }}>
                      Technical
                    </Typography>
                    {loading ? (
                      <>
                        <Skeleton height={20} width="70%" />
                        <Skeleton height={36} width="50%" />
                      </>
                    ) : (
                      <>
                        <List dense>
                          <ListItem>
                            <ListItemAvatar>
                              <Avatar>
                                <Inventory2Icon />
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={getMessage(locale, "techRiderLabel")}
                              secondary={getMessage(locale, artist.techRider?.url || artist.techRiderUrl ? "techRiderPdfAvailable" : "techRiderNotAvailable")}
                            />
                          </ListItem>
                        </List>
                        {technicalDownloadsEnabled && techRiderUrl && (
                          <Button
                            fullWidth
                            variant="outlined"
                            startIcon={<LinkIcon />}
                            disabled={documentDownloadBusy}
                            onClick={() => downloadTechnicalDocument((freshArtist) => freshArtist?.techRider?.url || freshArtist?.techRiderUrl)}
                            sx={{ mt: 1 }}
                          >
                            {getMessage(locale, "techRiderDownload")}
                          </Button>
                        )}
                        {technicalDownloadsEnabled && stageLayoutUrl && (
                          <>
                            <List dense>
                              <ListItem>
                                <ListItemAvatar>
                                  <Avatar><Inventory2Icon /></Avatar>
                                </ListItemAvatar>
                                <ListItemText
                                  primary={getMessage(locale, "stageLayoutLabel")}
                                  secondary={getMessage(locale, "techRiderPdfAvailable")}
                                />
                              </ListItem>
                            </List>
                            <Button
                              fullWidth
                              variant="outlined"
                              disabled={documentDownloadBusy}
                              onClick={() => downloadTechnicalDocument((freshArtist) => freshArtist?.stageLayout?.url || freshArtist?.stageLayoutUrl || freshArtist?.stagePlot?.url || freshArtist?.stagePlotUrl)}
                              startIcon={<LinkIcon />}
                            >
                              {getMessage(locale, "stageLayoutDownload")}
                            </Button>
                          </>
                        )}
                      </>
                    )}
                  </Paper>

                  {/* Availability */}
                  <Paper variant="outlined" sx={{ borderRadius: 3, p: 2, mt: 2 }}>
                    <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1 }}>
                      {getMessage(locale, "availabilityTitle")}
                    </Typography>
                    {loading ? (
                      <Skeleton variant="rounded" height={90} sx={{ borderRadius: 2 }} />
                    ) : (
                      <AvailabilityList availability={artist.availability} locale={locale} timeZone={artist.timeZone} />
                    )}
                    {hasPermission(user, PERMISSIONS.INVITE_ARTIST_TO_GIG) && <Button
                      fullWidth
                      variant="contained"
                      sx={{ mt: 1.5 }}
                      onClick={() => navigate(buildRecipientInboxPath("artist", artist.id))}
                    >
                      Invite to gig
                    </Button>}
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Media */}
          {tab === 1 && (
            <Box sx={{ p: 2 }}>
              {loading ? (
                <Grid container spacing={2}>
                  {Array.from({ length: 3 }).map((_, i) => (
                    <Grid size={{ xs: 12, sm: 6, md: 4 }} key={i}>
                      <Skeleton variant="rounded" height={200} sx={{ borderRadius: 3 }} />
                    </Grid>
                  ))}
                </Grid>
              ) : artist.photos?.length || artist.media?.length ? (
                <Stack spacing={3}>
                  <ProfileGallery photos={artist.photos} />
                  {artist.media?.length ? (
                    <Grid container spacing={2}>
                      {artist.media.map((m, i) => (
                        <Grid key={i} size={{ xs: 12, sm: 6, md: 4 }}>
                          <MediaCard m={m} />
                        </Grid>
                      ))}
                    </Grid>
                  ) : null}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {getMessage(locale, "profileNoMedia")}
                </Typography>
              )}
            </Box>
          )}

          {/* Reviews */}
          {tab === 2 && (
            <Box sx={{ p: 2 }}>
              {loading ? (
                <Stack spacing={1}>
                  <Skeleton height={72} />
                  <Skeleton height={72} />
                </Stack>
              ) : reviews?.length ? (
                <List>
                  {reviews.map((r) => (
                    <React.Fragment key={r.id}>
                      <ListItem alignItems="flex-start">
                        <ListItemAvatar>
                          <Avatar>
                            <StarRoundedIcon />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap" }}>
                              <Typography variant="subtitle2">{r.reviewer.name}</Typography>
                              <Chip
                                size="small"
                                label={`Prof ${r.ratings.professionalism}★`}
                                sx={{ ml: 0.5 }}
                              />
                              <Chip size="small" label={`Punct ${r.ratings.punctuality}★`} />
                              <Chip size="small" label={`Sound ${r.ratings.sound}★`} />
                              <Chip size="small" label={`Crowd ${r.ratings.crowd}★`} />
                              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                {formatDate(r.createdAt, { locale })}
                              </Typography>
                            </Stack>
                          }
                          secondary={r.text}
                        />
                      </ListItem>
                      <Divider component="li" />
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No reviews yet.
                </Typography>
              )}
              {hasPermission(user, PERMISSIONS.RATE_ARTIST_AFTER_GIG) && (
                <Button variant="outlined" sx={{ mt: 1 }} onClick={() => setReviewOpen(true)}>
                  {getMessage(locale, "reviewArtistTitle")}
                </Button>
              )}
            </Box>
          )}
        </Paper>

        <EventReviewDialog
          open={reviewOpen}
          onClose={() => setReviewOpen(false)}
          targetType="artist"
          targetId={id}
          onCreated={(review) => {
            setReviews((current) => [review, ...(current || [])]);
            setSnack({ open: true, msg: getMessage(locale, "reviewSuccess"), severity: "success" });
          }}
        />

        {/* Similar artists */}
        <Paper variant="outlined" sx={{ borderRadius: 3, p: 2, mt: 2 }}>
          <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1 }}>
            Similar artists
          </Typography>
          {loading ? (
            <Grid container spacing={2}>
              {Array.from({ length: 2 }).map((_, i) => (
                <Grid key={i} size={{ xs: 12, sm: 6, md: 4 }}>
                  <Skeleton variant="rounded" height={140} sx={{ borderRadius: 3 }} />
                </Grid>
              ))}
            </Grid>
          ) : similar?.length ? (
            <Grid container spacing={2}>
              {similar.map((a) => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={a.id}>
                  <SimilarArtistCard a={a} onOpen={(aid) => navigate(`/artists/${aid}`)} />
                </Grid>
              ))}
            </Grid>
          ) : (
            <Typography variant="body2" color="text.secondary">
              None found.
            </Typography>
          )}
        </Paper>

        {/* Snackbar */}
        <Snackbar
          open={snack.open}
          autoHideDuration={2200}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert severity={snack.severity} sx={{ width: "100%" }}>
            {snack.msg}
          </Alert>
        </Snackbar>

        <Box sx={{ height: 16 }} />
        </Box>
      </Container>
    </Box>
  );
}
